// INCLUDE THESE PAGES IN THE MAIN BUNDLE
// LAZY-LOAD OTHER PAGES USING import()
// ============================================
import nprogress from 'nprogress'
import Vue from 'vue'
import VueRouter from 'vue-router'

import PageLogin from '@cv/pages/PageLogin.vue'
import store from '@cv/store'
import { dbInitializedPromise } from '@cv/plugins/vuex-sync-documents'

nprogress.configure({
	showSpinner: false,
	minimum: 0.25,
	speed: 500,
	ease: 'ease'
})

Vue.use(VueRouter)

// Create a map to store scroll positions
const scrollPositions = new Map<string, { x: number; y: number }>()

const router = new VueRouter({
	scrollBehavior(to, from, storedPosition) {
		const scrollableDiv = document.querySelector('.app-content')
		if (!scrollableDiv) return

		// Store current scroll position before leaving
		if (from.path) {
			scrollPositions.set(from.path, {
				y: scrollableDiv.scrollTop,
				x: scrollableDiv.scrollLeft
			})
		}

		// On forward navigation (no stored position), scroll to top
		if (storedPosition) {
			// On back navigation, restore position
			const storedPosition = scrollPositions.get(to.path)
			if (storedPosition) {
				setTimeout(() => {
					scrollableDiv.scrollTo({
						top: storedPosition.y,
						left: storedPosition.x,
						behavior: 'instant'
					})
				})
			}
		} else {
			setTimeout(() => {
				scrollableDiv.scrollTo({
					top: 0,
					behavior: 'instant'
				})
			})
		}
	},
	mode: 'history',
	routes: [
		{
			name: 'login',
			alias: '/',
			path: '/manage',
			component: PageLogin,
			meta: {
				requireAuth: false,
				requireAccount: false,
				title: 'Login'
			}
		},
		{
			name: 'access',
			path: '/access',
			component: () => import('@cv/pages/PageAuthWithSubscriber.vue'),
			meta: {
				requireAuth: false,
				requireAccount: false
			}
		},
		{
			name: 'register',
			path: '/register',
			component: () => import('@cv/pages/PageRegister.vue'),
			meta: {
				requireAuth: false,
				requireAccount: false,
				title: 'Register'
			}
		},
		{
			name: 'recover-password',
			path: '/recover-password',
			component: () => import('@cv/pages/PageRecoverPassword.vue'),
			meta: {
				requireAuth: false,
				requireAccount: false,
				title: 'Recover Password'
			}
		},
		{
			name: 'media-outliers',
			path: '/media-outliers/:factor(by-subscribers|by-average-views)?/:outlierStatus(ignored|saved)?',
			component: () => import('@cv/pages/PageMediaOutliers.vue'),
			meta: {
				requireAuth: true,
				requireAccount: true,
				showMenu: 'account',
				title: 'Media Outliers'
			}
		},
		{
			name: 'vision-profiles',
			path: '/vision-profiles',
			component: () => import('@cv/pages/PageVisionProfiles.vue'),
			meta: {
				requireAuth: true,
				requireAccount: true,
				showMenu: 'account',
				title: 'Vision Profiles'
			}
		},
		{
			name: 'vision-reports',
			path: '/vision-reports',
			component: () => import('@cv/pages/PageVisionReports.vue'),
			meta: {
				requireAuth: true,
				requireAccount: true,
				showMenu: 'account',
				title: 'Vision Reports'
			}
		},
		{
			name: 'reset-password',
			path: '/reset-password/:email/:token',
			component: () => import('@cv/pages/PageResetPassword.vue'),
			meta: {
				requireAuth: false,
				requireAccount: false,
				disableRouteBasedOnAuthStatus: true,
				title: 'Reset Password'
			}
		},
		{
			name: 'accept-collaboration',
			path: '/accept/:collaborationId',
			component: () => import('@cv/pages/PageAcceptCollaboration.vue'),
			meta: {
				requireAuth: false,
				requireAccount: false
			}
		},
		{
			name: 'home',
			path: '/home',
			component: () => import('@cv/pages/PageHome.vue'),
			meta: {
				requireAuth: true,
				requireAccount: true,
				showMenu: 'account',
				tour: {
					name: 'app',
					target: '#tour-watch-tour'
				},
				search: {
					enabled: true,
					title: 'Home / Dashboard',
					description: 'Your main dashboard and overview',
					keywords: 'home dashboard overview main'
				}
			}
		},
		// Route for IDs (24 alphanumeric characters)
		{
			name: 'agreementById',
			path: '/agreement/:agreementId([A-Za-z0-9]{24})',
			component: () => import('@cv/pages/PageAgreement.vue'),
			meta: {
				requireAuth: false,
				requireAccount: false,
				showMenu: false,
				hidePrintHeader: true,
				title: 'Agreement'
			}
		},
		// Route for slugs (1 to 23 characters)
		{
			path: '/agreement/:slug',
			redirect: to => {
				// Convert the old route to the new format
				return `/A${to.params.slug}`
			}
		},
		// Route for slugs (1 to 23 characters)
		{
			name: 'agreementBySlug',
			path: '/A:slug',
			component: () => import('@cv/pages/PageAgreement.vue'),
			meta: {
				requireAuth: false,
				requireAccount: false,
				showMenu: false,
				hidePrintHeader: true,
				title: 'Agreement'
			}
		},

		{
			name: 'w9',
			path: '/collab/:collabId/w9',
			component: () => import('@cv/pages/PageTaxDocument.vue'),
			meta: {
				requireAuth: false,
				requireAccount: false,
				showMenu: false
			}
		},
		{
			name: 'app-config',
			path: '/app-config',
			component: () => import('@cv/pages/PageAppConfig.vue'),
			meta: {
				requireAuth: true,
				requireAccount: true,
				showMenu: 'account'
			}
		},
		{
			name: 'app-queue',
			path: '/app-queue',
			component: () => import('@cv/pages/PageAppQueue.vue'),
			meta: {
				requireAuth: true,
				requireAccount: true,
				showMenu: 'account'
			}
		},
		{
			name: 'errors',
			path: '/errors',
			component: () => import('@cv/pages/PageErrors.vue'),
			meta: {
				requireAuth: true,
				requireAccount: true,
				showMenu: 'account'
			}
		},
		{
			name: 'engagement',
			path: '/engagement',
			component: () => import('@cv/pages/PageEngagement.vue'),
			meta: {
				requireAuth: true,
				requireAccount: true,
				showMenu: 'account'
			}
		},
		{
			name: 'notifications',
			path: '/notifications',
			component: () => import('@cv/pages/PageNotifications.vue'),
			meta: {
				requireAuth: true,
				requireAccount: true,
				showMenu: 'account'
			}
		},
		{
			name: 'subscribed',
			path: '/subscribed',
			component: () => import('@cv/pages/PageEngagement.vue'),
			meta: {
				requireAuth: true,
				requireAccount: true,
				showMenu: 'account'
			}
		},
		{
			name: 'team',
			path: '/team',
			component: () => import('@cv/pages/PageTeam.vue'),
			meta: {
				requireAuth: true,
				requireAccount: true,
				showMenu: 'account',
				title: 'Team',
				search: {
					enabled: true,
					title: 'Team',
					description: 'Manage team members and permissions',
					keywords: 'team members users collaboration permissions'
				}
			}
		},
		{
			name: 'billing',
			path: '/billing',
			component: () => import('@cv/pages/PageBilling.vue'),
			meta: {
				requireAuth: true,
				requireAccount: true,
				showMenu: 'account',
				title: 'Billing',
				search: {
					enabled: true,
					title: 'Billing',
					description: 'Manage billing, invoices, and subscriptions',
					keywords: 'billing invoices subscription payment credit card'
				}
			}
		},
		{
			name: 'user',
			path: '/me',
			component: () => import('@cv/pages/PageUserSettings.vue'),
			meta: {
				requireAuth: true,
				requireAccount: false,
				showMenu: 'account',
				title: 'User settings',
				search: {
					enabled: true,
					title: 'User Settings',
					description: 'Manage your profile and account settings',
					keywords: 'settings profile user account personal preferences'
				}
			}
		},
		{
			name: 'user-settings',
			path: '/users/:userId',
			component: () => import('@cv/pages/PageUserSettings.vue'),
			meta: {
				requireAuth: true,
				requireAccount: false,
				showMenu: 'account',
				title: 'User settings'
			}
		},
		{
			name: 'users',
			path: '/users',
			component: () => import('@cv/pages/PageUsers.vue'),
			meta: {
				requireAuth: true,
				requireAccount: true,
				showMenu: 'account',
				title: 'Users'
			}
		},
		{
			name: 'account-settings',
			path: '/account/settings',
			component: () => import('@cv/pages/PageAccountSettings.vue'),
			meta: {
				requireAuth: true,
				requireAccount: true,
				showMenu: 'account',
				title: 'Account Settings'
			}
		},
		{
			name: 'topics',
			path: '/topics',
			component: () => import('@cv/pages/PageTopics.vue'),
			meta: {
				requireAuth: true,
				requireAccount: true,
				showMenu: 'account',
				title: 'Search Creators',
				tour: {
					name: 'app',
					target: '#search-container'
				}
			}
		},
		{
			name: 'media',
			path: '/media',
			component: () => import('@cv/pages/PageMedia.vue'),
			meta: {
				requireAuth: true,
				requireAccount: true,
				showMenu: 'account',
				title: 'Media'
			}
		},
		{
			name: 'social-entities',
			path: '/social-entities',
			component: () => import('@cv/pages/PageSocialEntities.vue'),
			meta: {
				requireAuth: true,
				requireAccount: true,
				showMenu: 'account',
				title: 'Social Channels'
			}
		},
		{
			name: 'creator',
			path: '/profile/:creatorId/:section?',
			alias: '/creators/:creatorId/:section?',
			component: () => import('@cv/pages/PageCreator.vue'),
			meta: {
				requireAuth: true,
				requireAccount: true,
				showMenu: 'account',
				title: route => store.getters['account/byId'](route.params.creatorId).name
			}
		},
		{
			name: 'collab',
			path: '/collab/:collabId/:section?',
			component: () => import('@cv/pages/PageCollab.vue'),
			meta: {
				requireAuth: true,
				requireAccount: true,
				showMenu: 'account',
				hidePrintHeader: true,
				title: route => {
					const collab = store.getters['collab/byId'](route.params.collabId)
					const creator = store.getters['account/byId'](collab?.creator)
					const project = store.getters['project/byId'](collab?.project)
					return [creator?.name, project?.name].filter(Boolean).join(' | ')
				}
			}
		},
		{
			name: 'collabs',
			path: '/collabs',
			component: () => import('@cv/pages/PageProject.vue'),
			meta: {
				requireAuth: true,
				requireAccount: true,
				showMenu: 'account',
				title: 'Collabs'
			}
		},
		{
			name: 'creators-to-review',
			path: '/creators-to-review',
			component: () => import('@cv/pages/PageCreators.vue'),
			meta: {
				requireAuth: true,
				requireAccount: true,
				showMenu: 'account',
				title: 'Creators to Review'
			}
		},
		{
			name: 'payments-and-taxes',
			path: '/payments-and-taxes/:section?/:a11?',
			component: () => import('@cv/pages/PageTaxDocuments.vue'),
			meta: {
				requireAuth: true,
				requireAccount: true,
				showMenu: 'account',
				title: 'Tax Documents'
			}
		},
		{
			name: 'creator-settings',
			path: '/creators/:creatorId/settings',
			component: () => import('@cv/pages/PageCreatorSettings.vue'),
			meta: {
				requireAuth: true,
				requireAccount: true,
				showMenu: 'account',
				title: 'Creators'
			}
		},
		{
			name: 'brand-settings',
			path: '/brands/:brandId',
			component: () => import('@cv/pages/PageBrandSettings.vue'),
			meta: {
				requireAuth: true,
				requireAccount: true,
				showMenu: 'account',
				title: 'Brands'
			}
		},
		{
			name: 'projects',
			path: '/projects',
			component: () => import('@cv/pages/PageProjects.vue'),
			meta: {
				requireAuth: true,
				requireAccount: true,
				showMenu: 'account',
				title: 'Projects',
				search: {
					enabled: true,
					title: 'Projects',
					description: 'View and manage all your projects',
					keywords: 'projects project management campaigns'
				}
			}
		},
		{
			name: 'project-settings',
			path: '/projects/:projectId/settings',
			component: () => import('@cv/pages/PageProjectSettings.vue'),
			meta: {
				requireAuth: true,
				requireAccount: true,
				showMenu: 'account',
				title: 'List Settings'
			}
		},
		{
			name: 'project',
			path: '/projects/:projectId',
			component: () => import('@cv/pages/PageProject.vue'),
			meta: {
				requireAuth: true,
				requireAccount: true,
				showMenu: 'account',
				printable: true,
				title: route => store.getters['project/byId'](route.params.projectId).name
			}
		},
		{
			name: 'project-notes',
			path: '/projects/:projectId/notes',
			component: () => import('@cv/pages/PageProjectNotes.vue'),
			meta: {
				requireAuth: true,
				requireAccount: true,
				showMenu: 'account',
				title: 'Project Notes'
			}
		},
		{
			name: 'project-performance',
			path: '/projects/:projectId/analytics',
			component: () => import('@cv/pages/PageProjectPerformance.vue'),
			meta: {
				requireAuth: true,
				requireAccount: true,
				showMenu: 'account',
				printable: true,
				title: route => {
					try {
						return (
							store.getters['project/byId'](route.params.projectId).name +
							' Analytics'
						)
					} catch (e) {
						return 'Project Analytics'
					}
				}
			}
		},
		{
			name: 'share-project',
			path: '/share-project/:projectId',
			component: () => import('@cv/pages/PageProjectPerformance.vue'),
			meta: {
				requireAuth: false,
				requireAccount: false,
				showMenu: false,
				showCVHeader: true,
				requireSubscriber: true,
				printable: false,
				title: route => {
					try {
						return (
							store.getters['project/byId'](route.params.projectId).name +
							' Analytics'
						)
					} catch (e) {
						return 'Project Analytics'
					}
				}
			}
		},
		{
			name: 'connections',
			path: '/business/connections',
			component: () => import('@cv/pages/PageConnections.vue'),
			meta: {
				requireAuth: true,
				requireAccount: true,
				showMenu: 'account',
				title: 'Services'
			}
		},
		{
			name: 'creators',
			path: '/creators',
			component: () => import('@cv/pages/PageCreators.vue'),
			meta: {
				requireAuth: true,
				requireAccount: true,
				showMenu: 'account',
				title: 'Search Results',
				tour: {
					name: 'app',
					target: '#tour-filter-search-results'
				}
			}
		},
		{
			name: 'payments',
			path: '/payments/:a11?',
			component: () => import('@cv/pages/PagePayments.vue'),
			meta: {
				requireAuth: true,
				requireAccount: true,
				showMenu: 'account',
				title: 'Payment History',
				search: {
					enabled: true,
					title: 'Payments/Taxes',
					description: 'View payment history, taxes, and payouts',
					keywords: 'payments taxes payouts history transactions money'
				}
			}
		},
		{
			name: 'brands',
			path: '/brands',
			component: () => import('@cv/pages/PageBrands.vue'),
			meta: {
				requireAuth: true,
				requireAccount: true,
				showMenu: 'account',
				title: 'Brands'
			}
		},
		{
			name: 'select-plan',
			path: '/select-plan',
			component: () => import('@cv/pages/PageSelectPlan.vue'),
			beforeEnter(to, from, next) {
				store
					.dispatch('account/hasAny')
					.then(hasAny => {
						if (hasAny) {
							return next({ name: 'home' })
						}
						next()
					})
					.catch(() => {
						next()
					})
			},
			meta: {
				requireAuth: true,
				showMenu: false,
				requireAccount: false
			}
		}
	]
})

router.beforeEach(async (to, from, next) => {
	store.commit('app/SET_ROUTE_LOADING', 'true')
	nprogress.start()
	try {
		store.commit('app/SET_FROM_ROUTE', from)
		if (to.meta.requireAuth) {
			if (to.query && to.query.u) {
				// this is a share link with an auth code
				await store.dispatch('user/loginWithAuthCode', to.query.u)
				return next({
					name: 'login',
					query: { redirect: to.path }
				})
			}
			try {
				// Wait for Dexie restoration to complete before authenticating
				// This prevents the authentication watcher from firing before all modules
				// are restored, which would cause RESET_LAST_LOAD_DATE to be overridden
				await dbInitializedPromise

				await store.dispatch('user/authenticate')
				if (to.meta.requireAccount && !(await store.dispatch('account/hasAny'))) {
					console.log(to.path, 'Route access denied. Account required.')
					if (to.name !== 'select-plan') {
						console.log('redirecting to select-plan')
						return next({ name: 'select-plan' })
					}
				}
				return next()
			} catch (e) {
				console.log(to.path, 'Route access denied. Authentication required.')
				return next({ name: 'login', query: { redirect: to.path } })
			}
		} else if (to.meta.requireSubscriber && !store.getters['subscriber/isSubscribed']) {
			const requireSubscriberRoute = {
				name: 'access',
				query: {
					redirect: to.path
				} as Record<string, string>
			}
			if (typeof to.meta.title === 'function') {
				requireSubscriberRoute.query.resource = to.meta.title(to)
			} else if (typeof to.meta.title === 'string') {
				requireSubscriberRoute.query.resource = to.meta.title
			}
			return next(requireSubscriberRoute)
		}
		return next()
	} catch (e) {
		console.error('router.beforeEach error', e)
		next('/')
	}
})

router.afterEach(() => {
	nprogress.done(true)
	store.commit('app/SET_ROUTE_LOADING', false)
})

router.onError(error => {
	console.error('Router error', error)
	store.commit('app/SET_ROUTE_LOADING', false)
	nprogress.done()
})

export default router
